/**
 * PhotosStep Component
 *
 * Step 4 of listing wizard: photo upload.
 * Images are compressed client-side to max 1920px width at 85% JPEG quality.
 */

import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { ListingFormData, ListingPhoto } from '../../hooks';

interface PhotosStepProps {
  data: ListingFormData;
  onAddPhoto: (photo: ListingPhoto) => void;
  onRemovePhoto: (photoId: string) => void;
  onReorderPhotos: (startIndex: number, endIndex: number) => void;
  onUpdatePhotoUrl: (photoId: string, url: string) => void;
}

const MAX_PHOTOS = 20;
const MIN_PHOTOS = 3;
const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB input allowed - will be compressed
const TARGET_MAX_WIDTH = 1920;
const TARGET_MAX_HEIGHT = 1440;
const JPEG_QUALITY = 0.85;

/** Compress image to max dimensions and JPEG quality, returns a new File */
const compressListingImage = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;

      // Scale down if larger than target
      if (width > TARGET_MAX_WIDTH || height > TARGET_MAX_HEIGHT) {
        const ratio = Math.min(TARGET_MAX_WIDTH / width, TARGET_MAX_HEIGHT / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }
          resolve(
            new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
              type: 'image/jpeg',
              lastModified: Date.now(),
            })
          );
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

export const PhotosStep: React.FC<PhotosStepProps> = ({
  data,
  onAddPhoto,
  onRemovePhoto,
  onReorderPhotos,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;

    const remainingSlots = MAX_PHOTOS - data.photos.length;
    const filesToProcess = Array.from(files)
      .filter((f) => f.type.startsWith('image/'))
      .slice(0, remainingSlots);

    if (filesToProcess.length === 0) return;
    setIsCompressing(true);

    for (const file of filesToProcess) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Maximum size is 30MB.`);
        continue;
      }

      try {
        const compressed = await compressListingImage(file);
        const photoId = `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const objectUrl = URL.createObjectURL(compressed);

        onAddPhoto({
          id: photoId,
          file: compressed,
          url: objectUrl,
          order: data.photos.length,
          isUploading: false,
        });
      } catch {
        toast.error(`Failed to process ${file.name}. Please try again.`);
      }
    }

    setIsCompressing(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDropOnPhoto = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (sourceIndex !== targetIndex) {
      onReorderPhotos(sourceIndex, targetIndex);
    }
    setDragOverIndex(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Add photos of your property
        </h3>
        <p className="text-gray-600">
          Add at least {MIN_PHOTOS} photos. The first photo will be your cover image.
        </p>
      </div>

      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          dragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />

        {isCompressing ? (
          <>
            <LoadingSpinner />
            <p className="text-gray-600 mt-3">Compressing images...</p>
          </>
        ) : (
          <>
            <PhotoIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">
              Drag and drop photos here, or{' '}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-primary-600 hover:underline font-medium"
              >
                browse
              </button>
            </p>
            <p className="text-sm text-gray-500">
              Any size accepted, auto-compressed to high quality. Max {MAX_PHOTOS} photos.
            </p>
          </>
        )}
      </div>

      {/* Photo Grid */}
      {data.photos.length > 0 && (
        <div>
          <p className="text-sm text-gray-600 mb-3">
            Drag photos to reorder. First photo is the cover.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.photos.map((photo, index) => (
              <div
                key={photo.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverIndex(index);
                }}
                onDragLeave={() => setDragOverIndex(null)}
                onDrop={(e) => handleDropOnPhoto(e, index)}
                className={`relative aspect-square rounded-xl overflow-hidden border-2 cursor-move transition-all ${
                  dragOverIndex === index
                    ? 'border-primary-500 scale-105'
                    : 'border-transparent'
                }`}
              >
                <img
                  src={photo.url}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover"
                />

                {/* Cover badge */}
                {index === 0 && (
                  <div className="absolute top-2 left-2 bg-white px-2 py-1 rounded-lg text-xs font-medium shadow">
                    Cover
                  </div>
                )}

                {/* Uploading overlay */}
                {photo.isUploading && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                )}

                {/* Remove button */}
                <button
                  type="button"
                  onClick={() => onRemovePhoto(photo.id)}
                  className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-red-600 transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>

                {/* Order number */}
                <div className="absolute bottom-2 left-2 w-6 h-6 bg-black/50 rounded-full text-white text-xs flex items-center justify-center">
                  {index + 1}
                </div>
              </div>
            ))}

            {/* Add more placeholder */}
            {data.photos.length < MAX_PHOTOS && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
              >
                <PlusIcon className="w-8 h-8 mb-1" />
                <span className="text-sm">Add more</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Validation Messages */}
      <div className="space-y-2">
        {data.photos.length > 0 && data.photos.length < MIN_PHOTOS && (
          <p className="text-sm text-yellow-600 flex items-center gap-2">
            <WarningIcon className="w-4 h-4" />
            Add at least {MIN_PHOTOS - data.photos.length} more photo(s) to publish
          </p>
        )}
        <p className="text-sm text-gray-500">
          {data.photos.length} of {MAX_PHOTOS} photos uploaded
        </p>
      </div>

      {/* Tips */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h4 className="font-medium text-gray-900 mb-2">Photo tips</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Use natural lighting for better photos</li>
          <li>• Show all rooms and outdoor spaces</li>
          <li>• Highlight unique features of your property</li>
          <li>• Keep photos horizontal/landscape oriented</li>
        </ul>
      </div>
    </div>
  );
};

// Icons
const PhotoIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const TrashIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const WarningIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const LoadingSpinner: React.FC = () => (
  <svg className="w-6 h-6 animate-spin text-primary-500" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

export default PhotosStep;
